import React, { useEffect, useState } from "react";
import { Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";
import { Link } from "react-router-dom";
import SimpleBar from "simplebar-react";
import classnames from "classnames";
import Spinners from "Components/Common/Spinner";
import { ChatSession } from "services/chatbotApi";

interface ChatBotListProps {
    userChatOpen: (chat: ChatSession) => void;
    createNewChat: () => void;
    deleteChat: (sessionId: string) => void;
    currentSessionId: string | null;
    currentDomain: Record<string, any>;
    chats: ChatSession[];
    loading: boolean;
}

const ChatBotList: React.FC<ChatBotListProps> = ({ 
    userChatOpen, 
    createNewChat, 
    deleteChat, 
    currentSessionId, 
    currentDomain, 
    chats, 
    loading 
}) => {
    const [menu1, setMenu1] = useState<boolean>(false);
    const [isLoading, setLoading] = useState(loading);

    useEffect(() => {
        setLoading(loading);
    }, [loading]);

    const currentBot = {
        name: `${currentDomain.name || "AI"} Bot`,
        isActive: true,
    };

    useEffect(() => {
        if (chats.length > 0) {
            userChatOpen(chats[0]);
        }
    }, [chats]);

    // Format time for display
    const formatTime = (timestamp: string) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Format date for display
    const formatDate = (timestamp: string) => {
        const date = new Date(timestamp);
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === now.toDateString()) {
            return formatTime(timestamp);
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Yesterday';
        } else {
            return date.toLocaleDateString();
        }
    };

    // Search functionality
    const searchUsers = () => {
        var input: any, filter: any, ul: any, li: any, a: any, i: any, txtValue: any;
        input = document.getElementById("search-user");
        filter = input.value.toUpperCase();
        ul = document.getElementById("recent-list");
        li = ul.getElementsByTagName("li");
        for (i = 0; i < li.length; i++) {
            a = li[i].getElementsByTagName("a")[0];
            txtValue = a.textContent || a.innerText;
            if (txtValue.toUpperCase().indexOf(filter) > -1) {
                li[i].style.display = "";
            } else {
                li[i].style.display = "none";
            }
        }
    };

    const handleDeleteChat = (e: React.MouseEvent, sessionId: string) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this chat?')) {
            deleteChat(sessionId);
        }
    };

    return (
        <React.Fragment>
            <div className="chat-leftsidebar me-lg-4">
                <div>

                    <div className="chat-leftsidebar-nav">
                        <div className="my-3 d-flex justify-content-between align-items-center">
                            <h4 className="fw-bold text-primary mb-0">Recents</h4>
                            <Button
                                color="primary"
                                size="sm"
                                onClick={createNewChat}
                                className="d-flex align-items-center"
                            >
                                <i className="bx bx-plus me-1"></i>
                                New
                            </Button>
                        </div>

                        <div className="search-box chat-search-box py-2">
                            <div className="position-relative">
                                <Input
                                    id="search-user"
                                    type="text"
                                    className="form-control"
                                    placeholder="Search chats..."
                                    onKeyUp={searchUsers}
                                />
                                <i className="bx bx-search-alt search-icon"></i>
                            </div>
                        </div>

                        <div>
                            <h5 className="font-size-14 mb-3">Recent</h5>
                            <ul className="list-unstyled chat-list position-relative" id="recent-list">
                                {isLoading && (!chats || chats.length === 0) ? (
                                    <Spinners setLoading={setLoading} />
                                ) : (
                                    <SimpleBar style={{ height: "410px" }}>
                                        {chats.length === 0 ? (
                                            <li className="text-center text-muted py-4">
                                                <p>No chats yet</p>
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={createNewChat}
                                                >
                                                    Start New Chat
                                                </Button>
                                            </li>
                                        ) : (
                                            chats.map((chat: ChatSession) => (
                                                <li
                                                    key={chat.id}
                                                    className={currentSessionId === chat.id ? "active chat-list-item-selected" : ""}
                                                    style={currentSessionId === chat.id ? {
                                                        background: "#e6f0ff",
                                                        borderLeft: "4px solid #28a745",
                                                        fontWeight: 600
                                                    } : {}}
                                                >
                                                    <Link to="#" onClick={() => userChatOpen(chat)} style={{ color: currentSessionId === chat.id ? "#28a745" : undefined }}>
                                                        <div className="d-flex">
                                                            <div className="avatar-xs align-self-center me-3">
                                                                <span
                                                                    className="avatar-title rounded-circle text-dark d-flex align-items-center justify-content-center"
                                                                    style={{ fontSize: 20, backgroundColor: "#f0f0f0", width: 36, height: 36, minWidth: 36, minHeight: 36, borderRadius: '50%' }}
                                                                >
                                                                    <i className="bx bx-message-dots"></i>
                                                                </span>
                                                            </div>
                                                            <div className="flex-grow-1 overflow-hidden">
                                                                <h5 className="text-truncate font-size-14 mb-1 text-capitalize">
                                                                    {chat.title || `Chat ${chat.id.slice(0, 8)}`}
                                                                </h5>
                                                                {chat.last_message && (
                                                                    <p className="text-muted text-truncate mb-0" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                                                                        {chat.last_message.content}
                                                                    </p>
                                                                )}
                                                            </div>
                                                            <div className="d-flex flex-column align-items-end">
                                                                <div className="font-size-11 text-muted" style={{ whiteSpace: 'nowrap', maxWidth: 60, overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 'normal' }}>
                                                                    {formatDate(chat.updated_at)}
                                                                </div>
                                                                {chat.unread_count > 0 && (
                                                                    <span className="badge bg-danger rounded-pill mt-1 fw-normal">
                                                                        {chat.unread_count}
                                                                    </span>
                                                                )}
                                                                {/* <Button
                                                                    color="danger"
                                                                    size="sm"
                                                                    className="btn-sm mt-1"
                                                                    onClick={(e) => handleDeleteChat(e, chat.id)}
                                                                >
                                                                    <i className="bx bx-trash"></i>
                                                                </Button> */}
                                                            </div>
                                                        </div>
                                                    </Link>
                                                </li>
                                            ))
                                        )}
                                    </SimpleBar>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default ChatBotList;